import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * The InlineGroup arranges items with defined horizontal spacing in a row.
 * If the items do not fit into one row, they will wrap to the next row with defined vertical spacing.
 * @see https://bronson.vwfs.tools/default/docs/objects/inline-group.html
 *
 * @param {('bottom'|'center'|'top')} [align='top'] - The vertical alignment of the items.
 * @param {boolean} [small] - Reduce the gap between the {@link InlineGroupItem}.
 * @param {boolean} [stretch] - Make the {@link InlineGroupItem} stretch to their maximum width.
 * @param {InlineGroupItem[]} [children] - A collection of {@link InlineGroupItem}.
 * @param {string} [className] - Additional class on the component element.
 * @param {string} [testId] - `[data-testid]` attribute on component element.
 * @param {Object} [otherProps] - Other props to be passed to component element.
 * @return {JSX.Element} - The {@link InlineGroup} element.
 * @constructor
 */
export function InlineGroup({ className, children, align = 'top', small, stretch, testId, ...otherProps }) {
  const classNameList = classNames('o-inline-group', className, {
    'o-inline-group--small': small, // Convenience prop
    'o-inline-group--stretch': stretch, // Convenience prop
    /**
     * Since the default for {@link align} is `top` no modifier class is needed.
     * Only applicable when {@link align} is set to either `bottom` or `center`.
     */
    [`o-inline-group--${align} `]: align === 'bottom' || align === 'center' ? align : null, // Convenience prop
  }).trim()

  return (
    <div className={classNameList} {...otherProps} data-testid={testId}>
      {children}
    </div>
  )
}

InlineGroup.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  testId: PropTypes.string,
  // Convenience props
  align: PropTypes.oneOf(['bottom', 'center', 'top']),
  small: PropTypes.bool,
  stretch: PropTypes.bool,
}

/**
 * Internal item for the {@link InlineGroup} component.
 *
 * @param {React.ReactNode} [children] - {@link InlineGroupItem} contents.
 * @returns {JSX.Element} – The {@link InlineGroupItem} component.
 * @constructor
 */
export function InlineGroupItem({ children }) {
  return <div className="o-inline-group__item">{children}</div>
}

InlineGroupItem.propTypes = {
  children: PropTypes.node,
}

InlineGroupItem.displayName = 'InlineGroupItem.Item'
InlineGroupItem.Item = InlineGroupItem
